import { z } from "zod";

/**
 * In zod v4, `.default({})` does not re-parse the provided value through the
 * inner schema, so nested field defaults are not applied. This helper wraps a
 * schema with `optional + transform` so that `undefined` (field absent) is
 * treated as `{}` and run through the full schema, applying all inner defaults.
 */
function withNestedDefault<T extends z.ZodTypeAny>(schema: T) {
  return z.optional(schema).transform((v) => schema.parse(v ?? {}) as z.output<T>);
}

export const ConsentBannerSchema = z.object({
  enabled: z.boolean().default(false),
  bannerText: z.string().max(500).default("This site uses cookies for analytics. Accept?"),
  acceptLabel: z.string().max(40).default("Accept"),
  declineLabel: z.string().max(40).default("Decline"),
  bannerColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
});
export type ConsentBanner = z.infer<typeof ConsentBannerSchema>;

const ga4Id = z.string().regex(/^G-[A-Z0-9]+$/);
const metaPixelId = z.string().regex(/^\d{10,20}$/);
const tiktokPixelId = z.string().regex(/^[A-Z0-9]{20}$/);
const gtmId = z.string().regex(/^GTM-[A-Z0-9]+$/);

export const AnalyticsConfigSchema = z.object({
  ga4: z.array(ga4Id).max(5).default([]),
  metaPixel: z.array(metaPixelId).max(5).default([]),
  tiktokPixel: z.array(tiktokPixelId).max(5).default([]),
  gtm: z.array(gtmId).max(3).default([]),
  consent: withNestedDefault(ConsentBannerSchema),
});
export type AnalyticsConfig = z.infer<typeof AnalyticsConfigSchema>;

/**
 * Reseller-scoped variant. Omits `consent` because Site-level always wins on
 * banner config (dealers may need to edit banner copy for local compliance).
 * Strict mode rejects a `consent` key submitted by accident.
 */
export const ResellerAnalyticsConfigSchema = AnalyticsConfigSchema.omit({
  consent: true,
}).strict();
export type ResellerAnalyticsConfig = z.infer<typeof ResellerAnalyticsConfigSchema>;

const _IntegrationsInner = z.object({
  analytics: withNestedDefault(AnalyticsConfigSchema),
});

export const IntegrationsSchema = withNestedDefault(_IntegrationsInner);
export type IntegrationsConfig = z.infer<typeof IntegrationsSchema>;
